// Input handler. Maps physical keys (KeyboardEvent.code) to logical actions.
// Supports remapping and per-action repeat semantics.

export type Action =
  | 'left' | 'right' | 'softDrop' | 'hardDrop'
  | 'rotateCW' | 'rotateCCW' | 'rotate180'
  | 'hold' | 'pause' | 'restart' | 'menu';

export const DEFAULT_KEYMAP: Record<Action, string[]> = {
  left: ['ArrowLeft'],
  right: ['ArrowRight'],
  softDrop: ['ArrowDown'],
  hardDrop: ['Space'],
  rotateCW: ['ArrowUp', 'KeyX'],
  rotateCCW: ['KeyZ', 'ControlLeft', 'ControlRight'],
  rotate180: ['KeyA', 'ShiftLeft'],
  hold: ['KeyC', 'ShiftRight'],
  pause: ['Escape', 'F1'],
  restart: ['KeyR'],
  menu: [], // reserved (Esc already handled by 'pause'); keep slot for users
};

export type ActionHandler = (action: Action, pressed: boolean, repeat: boolean, e: KeyboardEvent) => void;

export class Input {
  keymap: Record<Action, string[]> = structuredClone(DEFAULT_KEYMAP);
  private pressed = new Set<string>();
  private down = new Set<Action>();
  private handler: ActionHandler | null = null;
  private capturing: ((code: string, e: KeyboardEvent) => void) | null = null;
  private listening = false;
  // For tracking input device & finesse hints (optional UI):
  lastInputDevice: 'keyboard' | 'touch' = 'keyboard';

  setKeymap(m: Partial<Record<Action, string[]>>): void {
    this.keymap = { ...this.keymap, ...m };
  }

  resetKeymap(): void { this.keymap = structuredClone(DEFAULT_KEYMAP); }

  attach(target: Window | HTMLElement = window): void {
    if (this.listening) return;
    this.listening = true;
    target.addEventListener('keydown', this.onKeyDown as EventListener);
    target.addEventListener('keyup', this.onKeyUp as EventListener);
    target.addEventListener('blur', this.onBlur as EventListener);
    document.addEventListener('visibilitychange', this.onVisibility);
  }

  detach(target: Window | HTMLElement = window): void {
    target.removeEventListener('keydown', this.onKeyDown as EventListener);
    target.removeEventListener('keyup', this.onKeyUp as EventListener);
    target.removeEventListener('blur', this.onBlur as EventListener);
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.listening = false;
  }

  setHandler(h: ActionHandler | null): void { this.handler = h; }

  /** Start capturing the next key press to a callback (for rebinding UI). */
  captureNext(cb: (code: string, e: KeyboardEvent) => void): () => void {
    this.capturing = cb;
    return () => { this.capturing = null; };
  }

  /** Get the action(s) currently held down. */
  heldActions(): ReadonlySet<Action> { return this.down; }

  /** Force-release all keys (e.g., on focus loss). */
  releaseAll(): void {
    this.pressed.clear();
    for (const a of [...this.down]) {
      this.down.delete(a);
      this.handler?.(a, false, false, new KeyboardEvent('keyup'));
    }
  }

  // ---------- internals ----------
  private actionsForCode(code: string): Action[] {
    const out: Action[] = [];
    for (const a of Object.keys(this.keymap) as Action[]) {
      if (this.keymap[a].includes(code)) out.push(a);
    }
    return out;
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    if (e.code === 'F5' || e.code === 'F11' || e.code === 'F12') return;
    if (this.capturing) {
      e.preventDefault();
      if (e.code === 'Escape') { this.capturing = null; return; }
      const cb = this.capturing;
      this.capturing = null;
      cb(e.code, e);
      return;
    }
    // Don't swallow keystrokes that are headed for a text input — the
    // global game keymap binds A/Z/X/C/R/etc. to actions and would
    // otherwise prevent default and stop the character from reaching
    // the focused input. Same for select / textarea / contenteditable.
    if (isTextEditable(e.target)) return;
    const acts = this.actionsForCode(e.code);
    if (acts.length === 0) return;
    e.preventDefault();
    const repeat = this.pressed.has(e.code);
    this.pressed.add(e.code);
    this.lastInputDevice = 'keyboard';
    for (const a of acts) {
      if (!repeat) this.down.add(a);
      this.handler?.(a, true, repeat, e);
    }
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    if (isTextEditable(e.target)) return;
    const acts = this.actionsForCode(e.code);
    if (acts.length === 0) return;
    e.preventDefault();
    this.pressed.delete(e.code);
    for (const a of acts) {
      this.down.delete(a);
      this.handler?.(a, false, false, e);
    }
  };

  private onBlur = (): void => this.releaseAll();
  private onVisibility = (): void => { if (document.visibilityState !== 'visible') this.releaseAll(); };
}

/** Returns true if the event target is a focused text input, textarea,
 *  select, or contenteditable element. The game's global keymap should
 *  not intercept those keystrokes — let the browser deliver them
 *  normally so the user can type. */
function isTextEditable(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT') {
    const t = (target as HTMLInputElement).type;
    // Only text-like inputs care about character keys. Range / checkbox
    // / button etc. should still hand the keys back to the game.
    return t === 'text' || t === 'search' || t === 'email' || t === 'password'
        || t === 'number' || t === 'tel' || t === 'url' || t === '';
  }
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  return false;
}

export function keyLabel(code: string): string {
  if (code.startsWith('Key')) return code.slice(3);
  if (code.startsWith('Digit')) return code.slice(5);
  if (code.startsWith('Arrow')) return code.slice(5).toUpperCase();
  if (code === 'Space') return 'SPACE';
  if (code === 'Escape') return 'ESC';
  if (code === 'ShiftLeft') return 'L SHIFT';
  if (code === 'ShiftRight') return 'R SHIFT';
  if (code === 'ControlLeft') return 'L CTRL';
  if (code === 'ControlRight') return 'R CTRL';
  if (code === 'AltLeft') return 'L ALT';
  if (code === 'AltRight') return 'R ALT';
  if (code === 'MetaLeft' || code === 'MetaRight') return 'META';
  if (code === 'Tab') return 'TAB';
  if (code === 'Enter') return 'ENTER';
  if (code === 'Backspace') return 'BSP';
  return code.toUpperCase();
}
